import { Button, Col, Row, Stack } from "react-bootstrap";
import RATooltip from "../RATooltip";
import { getSongDeltaColorCss, getSongScoreColorCss, mapItemType, mapItemTypeColor, toAutoDecimals } from "../HelperFunctions";
import { Await, useRouteLoaderData } from "react-router-dom";
import { Suspense, useMemo, useState } from "react";
import { ClockFill } from "react-bootstrap-icons";
import BreakSynopsis from "./BreakSynopsis";
import AudioPlayer from "../AudioPlayer";
import moment from "moment/min/moment-with-locales";
import { axiosInstance } from "../../auth/Auth";
import ClocksModal from "../ClocksModal";
import BreakKeywords from "./BreakKeywords";
import BreakContentGroups from "./BreakContentGroups";
import { useResolvedModules } from "../HelperHooks";

export default function Break({ formData, radioId, dataType, item, virtualizer, virtualRow, setBreaksData, filterFunction = () => true }) {
    const { radiosRecordSettings } = useRouteLoaderData("root");
    const [showClocksLight, setShowClocksLight] = useState(false);

    const resolvedModules = useResolvedModules();

    const clocksEnabled = useMemo(() => resolvedModules?.includes(1), [resolvedModules]);

    async function getAudioUrl(startDatetime, endDatetime) {
        try {
            const response = await axiosInstance.get(`/mp3service/?radio_id=${radioId}&start_time=${startDatetime}&end_time=${endDatetime}`);
            return response.data;
        } catch (error) {
            console.error(error);
        }
    }

    async function hideShowBreak(action, break_id) {
        try {
            setBreaksData(current => ({
                ...current,
                breaks: current.breaks.map(b => (b.break_id === break_id ? { ...b, hidden: action } : b)),
            }));
            await axiosInstance.post(`/content_testing/breaks/toggle_hide?hidden=${action}`, [break_id]);
        } catch (error) {
            console.error(error);
        }
    }

    function onKeywordsUpdate(keywords) {
        setBreaksData(current => ({
            ...current,
            breaks: current.breaks.map(b => (b.break_id === item.break_id ? { ...b, keywords: keywords } : b)),
        }));
    }

    function onContentGroupsUpdate(contentGroups) {
        setBreaksData(current => ({
            ...current,
            breaks: current.breaks.map(b => (b.break_id === item.break_id ? { ...b, content_groups: contentGroups } : b)),
        }));
    }

    return (
        <Row
            data-index={virtualRow?.index}
            ref={virtualizer?.measureElement}
            style={{ transform: `translateY(${virtualRow?.start - virtualizer?.options.scrollMargin}px)` }}
            className={`py-2 border-bottom ${virtualizer && "position-absolute"} pe-2 w-100 ${
                item.hidden ? "bg-grayed-out" : item.excluded ? "bg-blued-out" : ""
            }`}>
            <RATooltip
                className="item-type-bar p-0 rounded-1 align-self-stretch"
                style={{ borderLeftColor: mapItemTypeColor(item.item_type) }}
                tooltipElement={<Col xs={"auto"}></Col>}>
                {mapItemType(item.item_type)}
            </RATooltip>
            <Col xs={"auto"} className=" align-self-center">
                <Col xs={12} className="text-center">
                    <Suspense>
                        <Await resolve={radiosRecordSettings}>
                            {resolvedRecordSettings => (
                                <AudioPlayer
                                    getAudioUrl={async (...args) => await getAudioUrl(item.start_datetime, item.end_datetime, ...args)}
                                    streamDelay={0}
                                    disabled={
                                        resolvedRecordSettings.data.find(x => x.radio_id === radioId)?.audio_days_stored <=
                                        (new Date() - new Date(item.start_datetime)) / (1000 * 60 * 60 * 24)
                                            ? true
                                            : false
                                    }
                                />
                            )}
                        </Await>
                    </Suspense>
                </Col>
                {item.item_type !== "c" && (
                    <Col xs={12} className="text-center">
                        <BreakSynopsis text={item.synopsis} />
                    </Col>
                )}
            </Col>
            <Col>
                <Row className="align-items-center h-100 gy-2 gy-lg-0">
                    <Col xs lg={3} className="text-center text-lg-start">
                        <Stack direction="horizontal" gap={1}>
                            <span>{moment(item.start_datetime).format("L")}</span>
                            <span className="fw-bold">{moment(item.start_datetime).format("LTS")}</span>
                            <Button
                                size="sm"
                                variant="contained"
                                className="border-0 p-0 d-flex btn-download"
                                onClick={() => setShowClocksLight(true)}
                                disabled={!clocksEnabled}>
                                <ClockFill width={15} height={15} />
                            </Button>
                            <ClocksModal
                                show={showClocksLight}
                                handleClose={() => setShowClocksLight(false)}
                                radioId={radioId}
                                date={moment(item.start_datetime).toDate()}
                                hour={moment(item.start_datetime).hour()}
                                startAudioTime={moment(item.start_datetime).diff(moment(item.start_datetime).startOf("hour"), "seconds")}
                                addToCollectionAllowed={true}
                                fromContentTesting={true}
                                formData={formData}
                            />
                        </Stack>
                    </Col>
                    <Col xs={3} lg={1} className="text-center">
                        {moment.utc(moment.duration(item.duration, "seconds").asMilliseconds()).format("m:ss")}
                    </Col>
                    <Col xs={3} lg={1} className="d-flex justify-content-center">
                        {dataType === 1 ? (
                            <div className={`score ${getSongScoreColorCss(item.score, true)} ${item.invalid ? "invalid" : ""}`}>
                                {toAutoDecimals(item.score, 1)}
                            </div>
                        ) : (
                            <div className={`fw-bold ${getSongDeltaColorCss(item.delta)} ${item.invalid ? "invalid" : ""}`}>{toAutoDecimals(item.delta)}</div>
                        )}
                    </Col>

                    <Col xs={12} lg className="text-center ">
                        <BreakContentGroups item={item} onContentGroupsUpdate={onContentGroupsUpdate} radioId={radioId} filterFunction={filterFunction} />
                    </Col>
                    <Col xs="auto">
                        <Button
                            size="sm"
                            variant={`${item.hidden ? "ra-dark-blue" : "ra-dark-blue-outline"}`}
                            className="py-0"
                            title="Hide/Show break"
                            onClick={() => {
                                hideShowBreak(!item.hidden, item.break_id);
                            }}>
                            {item.hidden ? "Show" : "Hide"}
                        </Button>
                    </Col>
                    <Col xs={12} className="text-center">
                        <BreakKeywords item={item} onKeywordsUpdate={onKeywordsUpdate} />
                    </Col>
                </Row>
            </Col>
        </Row>
    );
}
